import {
    collection,
    doc,
    type FirestoreDataConverter,
    getDoc,
    QueryDocumentSnapshot,
    type SnapshotOptions,
} from "firebase/firestore";
import { Timestamp } from "firebase/firestore";

import { db } from "@/lib/firebase/client.ts";
import type { MonthlySession, Session, UserSessionsByMonth } from "@/models/session";
import { DatabaseRepository } from "@/repositories/database-repository.ts";

const sessionConverter: FirestoreDataConverter<Session> = {
    toFirestore(session: Session) {
        return { ...session };
    },
    fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): Session {
        const data = snapshot.data(options)!;
        return {
            uid: snapshot.id, // always safe
            ...data,
            dates: data.dates.map((d: Timestamp) => d.toDate()).sort((a: Date, b: Date) => a.getTime() - b.getTime()),
            signUpAt: (data.signUpAt as Timestamp)?.toDate(),
            createdAt: (data.createdAt as Timestamp)?.toDate(),
            updatedAt: (data.updatedAt as Timestamp)?.toDate(),
        } as Session;
    },
};

export class SessionRepository extends DatabaseRepository<Session> {
    private static instance: SessionRepository;
    protected readonly collection = collection(db, "sessions").withConverter(sessionConverter);

    private constructor() {
        super();
    }

    public static getInstance(): SessionRepository {
        if (!SessionRepository.instance) {
            SessionRepository.instance = new SessionRepository();
        }
        return SessionRepository.instance;
    }

    async getById(uid: string): Promise<Session | null> {
        const ref = doc(this.collection, uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) return null;
        return snap.data() satisfies Session;
    }

    /**
     * # Helper functions
     **/

    /**
     * ### Helper function to group sessions by month
     *
     * This function groups sessions by month.
     *
     * @param sessions
     * @returns Record<string, MonthlySession[]>
     *     e.g. { "2024-01": [{ dates: [Date], ...session }], "2024-02": [{ dates: [Date], ...session }], ... }
     **/
    static groupSessionsByMonth(sessions: Session[]) {
        const grouped: Record<string, MonthlySession[]> = {};

        sessions.forEach((session) => {
            const monthMap: Record<string, Date[]> = this.groupDateByMonth(session.dates);

            for (const [month, dates] of Object.entries(monthMap)) {
                dates.sort((a, b) => a.getTime() - b.getTime());
                if (!grouped[month]) grouped[month] = [];
                grouped[month].push({ ...session, dates });
            }
        });

        return grouped;
    }

    static groupSessionsByUserAndMonth(sessions: Session[]) {
        const sortedSessions = SessionRepository.sortSessionsByDate(sessions);
        const grouped: UserSessionsByMonth = {};

        sortedSessions.forEach((session) => {
            if (!grouped[session.modelId]) grouped[session.modelId] = {};

            const monthMap: Record<string, Date[]> = SessionRepository.groupDateByMonth(session.dates);

            // For each month, push a session copy with only those dates
            for (const [month, dates] of Object.entries(monthMap)) {
                if (!grouped[session.modelId][month]) grouped[session.modelId][month] = [];
                grouped[session.modelId][month].push({ ...session, dates });
            }
        });
        return grouped;
    }

    static groupDateByMonth(dates: Date[]) {
        const grouped: Record<string, Date[]> = {};
        dates.forEach((date) => {
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
            if (!grouped[monthKey]) grouped[monthKey] = [];
            grouped[monthKey].push(date);
        });
        return grouped;
    }

    static sortSessionsByDate(sessions: Session[]) {
        return sessions.sort((a, b) => {
            if (a.dates.length === 0 || b.dates.length === 0) return 0;
            return a.dates[0].getTime() - b.dates[0].getTime();
        });
    }
}
